/**
 * Section-based Panel Calc (Visualizer v2)
 * ========================================
 *
 * Stage 1 of the section pipeline's costing: compute W&CB and SlatWall PANEL
 * quantities from the canonical positioned-section model, by resolving each
 * surface (carving W&CB fills with SlatWall inserts) and running the EXISTING
 * panel math per resolved sub-rectangle. Because it reuses `panels.ts` and
 * `slatwall.ts` with the same inputs the current engine uses (net width × band
 * height), it reproduces today's panel BoM for any band-equivalent config.
 *
 * Scope (per docs/VISUALIZER-V2-PLAN.md §M): PANELS first. The multi-edge
 * SlatWall↔W&CB interface trims, J/base trims, screws, and garage/openings are
 * handled in later milestones. H-trims that fall directly out of the existing
 * panel functions are carried through but are not the parity target yet.
 *
 * Branch-exclusive to `3d-visualizer-v3`.
 */

import { MaterialItem } from '../../types';
import { RoomSurfaces, Surface, Section, roomSurfaceList } from '../../types/sections';
import { resolveSurface, Rect, rectOf } from './resolver';
import { layoutCourses, groupRuns, holesOver } from './courses';
import { surfaceOutline } from './outline';
import {
  calculateHorizontalPanels,
  calculateVerticalPanels,
  calculateVerticalStackingHTrims,
} from '../panels';
import { calculateSlatWallPanels, SlatWallOffcut } from '../slatwall';
import { SLATWALL_COVERAGE_HEIGHT, TRIM_STOCK_LENGTH, calculatePanelStripsForWidth } from '../constants';
import { getWCBProduct, getHTrimProduct } from '../../data/productTable';
import { optimizeWcbRun } from './wcbOptimizer';

export interface SectionPanelResult {
  /** W&CB panel line items. */
  wcbPanels: MaterialItem[];
  /** SlatWall panel line items. */
  slatWallPanels: MaterialItem[];
  /** H-trims that fall directly out of the panel functions (not the parity target yet). */
  hTrims: MaterialItem[];
}

/** Min run length (ft) for the opt-in horizontal optimizer; sub-8' keeps the existing multi-cut path. */
const MIN_OPTIMIZER_RUN_FEET = 8;

/** Panels for one resolved W&CB region, honoring the section's orientation. */
function wcbRegionPanels(region: Rect, section: Section): { panels: MaterialItem[]; hTrims: MaterialItem[] } {
  const color = section.color;
  if (section.orientation === 'Vertical') {
    return {
      panels: calculateVerticalPanels(region.width, region.height, color, 'WCB_Section'),
      hTrims: calculateVerticalStackingHTrims(region.height, region.width, color),
    };
  }

  // Horizontal. Opt-in optimizer (§F): used only when the section explicitly sets a
  // length-lock or a non-default strategy, and the run is >= 8'. Otherwise the existing
  // least-waste path runs, preserving parity with the current engine (vertical too).
  const wantsOptimizer =
    region.width >= MIN_OPTIMIZER_RUN_FEET &&
    ((section.lengthLock != null && section.lengthLock > 0) ||
      (section.layoutStrategy != null && section.layoutStrategy !== 'least-waste'));

  if (wantsOptimizer) return optimizedHorizontalPanels(region, section);

  const r = calculateHorizontalPanels(region.width, region.height, color, null, 'WCB_Section');
  return { panels: r.panels, hTrims: r.hTrims };
}

/** Build horizontal W&CB panels + H-trim from the §F optimizer's chosen layout. */
function optimizedHorizontalPanels(region: Rect, section: Section): { panels: MaterialItem[]; hTrims: MaterialItem[] } {
  const color = section.color;
  const numRows = calculatePanelStripsForWidth(region.height);

  const layout = optimizeWcbRun(region.width, {
    strategy: section.layoutStrategy ?? 'fewest-joints',
    lengthLock: section.lengthLock ?? null,
    // Live, retailer-hydrated pricing (§N) — reads the loaded product table.
    panelPrice: (len) =>
      getWCBProduct(len as 8 | 10 | 12 | 14 | 16 | 20, color)?.pricingUSD ?? Number.POSITIVE_INFINITY,
    jointCost: getHTrimProduct(color)?.pricingUSD ?? 0,
  });

  // Panels: per distinct stock length × number of rows.
  const perRowCounts = new Map<number, number>();
  for (const len of layout.lengths) perRowCounts.set(len, (perRowCounts.get(len) ?? 0) + 1);

  const panels: MaterialItem[] = [];
  for (const [len, perRow] of perRowCounts) {
    const product = getWCBProduct(len as 8 | 10 | 12 | 14 | 16 | 20, color);
    if (product && perRow * numRows > 0) {
      panels.push({
        productNumber: product.productNumber,
        productID: product.productID,
        name: product.name,
        color: product.color,
        quantity: perRow * numRows,
        lengthFeet: product.lengthFeet,
        sqft: product.sqft,
        type: 'WCB_Surface',
      });
    }
  }

  // H-trim: one vertical run per joint, spanning the section height (matches the engine).
  const hTrims: MaterialItem[] = [];
  if (layout.joints > 0) {
    const hTrimProduct = getHTrimProduct(color);
    if (hTrimProduct) {
      const hTrimQuantity = Math.ceil((layout.joints * region.height) / TRIM_STOCK_LENGTH);
      if (hTrimQuantity > 0) {
        hTrims.push({
          productNumber: hTrimProduct.productNumber,
          productID: hTrimProduct.productID,
          name: hTrimProduct.name,
          color: hTrimProduct.color,
          quantity: hTrimQuantity,
          lengthFeet: hTrimProduct.lengthFeet,
          sqft: 0,
          type: 'H_Trim_Surface_Join',
        });
      }
    }
  }

  return { panels, hTrims };
}

/**
 * Compute panel quantities for a single surface. `offcuts` is the shared,
 * project-wide SlatWall offcut pool — pass the SAME array across surfaces (in
 * wall order) to mirror the current engine's cross-wall offcut reuse.
 */
export function calculateSurfacePanels(surface: Surface, offcuts: SlatWallOffcut[]): SectionPanelResult {
  const resolved = resolveSurface(surface);
  const out: SectionPanelResult = { wcbPanels: [], slatWallPanels: [], hTrims: [] };
  // A wall under a sloped ceiling: its boards are cut to the rake (outline.ts).
  const outline = surfaceOutline(surface);

  for (const fill of resolved.fills) {
    if (fill.section.productType !== 'WCB') continue;

    // Nothing over it on a plain rectangle: counted exactly as the current
    // engine counts it (this is the path the parity tests hold to).
    if (fill.holes.length === 0 && !outline) {
      for (const region of fill.regions) {
        const { panels, hTrims } = wcbRegionPanels(region, fill.section);
        out.wcbPanels.push(...panels);
        out.hTrims.push(...hTrims);
      }
      continue;
    }

    // Something sits on it (a SlatWall inset, a window or door), or the wall
    // has a shaped top. Count by COURSE: lay the 16" rows / strips across the
    // whole section, keep the runs that still need board, and count each group
    // of equal-length runs with the same panel maths — "this many courses of
    // this length". See courses.ts.
    const runs = layoutCourses(rectOf(fill.section), fill.section.orientation ?? 'Horizontal', fill.holes, undefined, outline);
    for (const group of groupRuns(runs)) {
      // The maths takes (length, depth) and derives the course count from the
      // depth; shave a hair off so a whole number of courses can't round up.
      const depth = group.depth - 1e-6;
      const asRegion: Rect = fill.section.orientation === 'Vertical'
        ? { x: 0, y: 0, width: depth, height: group.length }
        : { x: 0, y: 0, width: group.length, height: depth };
      const { panels, hTrims } = wcbRegionPanels(asRegion, fill.section);
      out.wcbPanels.push(...panels);
      out.hTrims.push(...hTrims);
    }
  }

  for (const insert of resolved.inserts) {
    if (insert.productType !== 'SlatWall') continue;
    const cut = holesOver(rectOf(insert), resolved.openings);

    if (cut.length === 0) {
      // Section height is whole 6" rows by construction (height = numRows × 0.5').
      const numRows = Math.round(insert.height / SLATWALL_COVERAGE_HEIGHT);
      const r = calculateSlatWallPanels(insert.height, insert.width, insert.color, numRows, offcuts);
      out.slatWallPanels.push(...r.panels);
      out.hTrims.push(...r.hTrims);
      continue;
    }

    // A window or door cuts into it: count by 6" row, the same way boards are
    // counted around an inset — rows the opening covers completely are
    // interrupted, rows it only bites into stay whole.
    const runs = layoutCourses(rectOf(insert), 'Horizontal', cut, SLATWALL_COVERAGE_HEIGHT, outline);
    for (const group of groupRuns(runs)) {
      const r = calculateSlatWallPanels(group.courses * SLATWALL_COVERAGE_HEIGHT, group.length, insert.color, group.courses, offcuts);
      out.slatWallPanels.push(...r.panels);
      out.hTrims.push(...r.hTrims);
    }
  }

  return out;
}

/**
 * Compute panel quantities for the whole room. Walls are processed in order
 * with a single shared SlatWall offcut pool (matching `calculateMultipleWalls`).
 */
export function calculateRoomPanels(room: RoomSurfaces): SectionPanelResult {
  const offcuts: SlatWallOffcut[] = [];
  const out: SectionPanelResult = { wcbPanels: [], slatWallPanels: [], hTrims: [] };

  const surfaces: Surface[] = roomSurfaceList(room);
  for (const surface of surfaces) {
    const r = calculateSurfacePanels(surface, offcuts);
    out.wcbPanels.push(...r.wcbPanels);
    out.slatWallPanels.push(...r.slatWallPanels);
    out.hTrims.push(...r.hTrims);
  }

  return out;
}
